import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, FileText, CheckCircle2, Clock, ChevronRight, ArrowLeft, Save, FileSignature, Info } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Select } from '@/components/ui/Select'
import { AsyncSelect } from '@/components/ui/AsyncSelect'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Badge } from '@/components/ui/Badge'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import {
  MedicalAppointmentsService,
  type MedicalAppointment,
  type MedicalAppointmentStatus,
} from '@/services/modules/MedicalAppointmentsService'
import { maskCpf } from '@/lib/format'
import { toast } from '@/components/ui/Toast'
import { useAuthority } from '@/hooks/useAuthority'

const PENDING = 'AGUARDANDO CONFIRMAÇÃO'
const CONFIRMED = 'CONFIRMADO'

const STATUS_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: PENDING, label: 'Pendentes' },
  { value: CONFIRMED, label: 'Confirmadas' },
]

/** Status por extenso (mesmo rótulo do badge) — usado na exportação. */
function statusLabel(status: MedicalAppointmentStatus): string {
  if (status === CONFIRMED) return 'Confirmada'
  if (status === 'CANCELADO') return 'Cancelada'
  return 'Pendente'
}

function StatusBadge({ status }: { status: MedicalAppointmentStatus }) {
  if (status === CONFIRMED) return <Badge variant="green"><CheckCircle2 size={12} className="mr-1" />Confirmada</Badge>
  if (status === 'CANCELADO') return <Badge variant="red">Cancelada</Badge>
  return <Badge variant="amber"><Clock size={12} className="mr-1" />Pendente</Badge>
}

interface Option { value: string; label: string }

export function MedicalAppointmentsListPage() {
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar por médico', initialStatus: '', statusOptions: STATUS_OPTIONS })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(MedicalAppointmentsService, { size: 15, reloadKey: reload, filters: sf.filters })
  const { can } = useAuthority()
  const canCreate = can('medical_appointments.appointments.create')
  // Editar e confirmar usam a mesma permissão (PUT e POST …/confirm).
  const canEdit = can('medical_appointments.appointments.edit')
  const canDelete = can('medical_appointments.appointments.delete')
  // Agendamento CONFIRMADO: a rota de exclusão é a mesma (delete), e a tela
  // ainda exige a permissão específica de excluir confirmada.
  const canDeleteConfirmed = canDelete && can('medical_appointments.appointments.delete_confirmed')
  const canPdf = can('medical_appointments.appointments.confirmation_pdf')
  const canCustom = can('medical_appointments.appointments.custom_confirmation')

  const exp = useDataTableExport({
    filename: 'agendamentos',
    service: MedicalAppointmentsService,
    filters: sf.filters,
    columns: [
      { header: 'Paciente',       accessor: (a: MedicalAppointment) => a.subscriber?.name ?? '' },
      { header: 'Credenciado',    accessor: (a: MedicalAppointment) => a.provider?.name ?? '' },
      { header: 'Especialidade',  accessor: (a: MedicalAppointment) => a.specialty?.specialty ?? '' },
      { header: 'Data',           accessor: (a: MedicalAppointment) => a.formatted?.appointmentDate ?? '' },
      { header: 'Horário',        accessor: (a: MedicalAppointment) => a.formatted?.appointmentTime ?? '' },
      { header: 'Médico',         accessor: (a: MedicalAppointment) => a.doctorName ?? '' },
      { header: 'Status',         accessor: (a: MedicalAppointment) => statusLabel(a.status) },
      { header: 'Criado por',     accessor: (a: MedicalAppointment) => a.creator?.name ?? '' },
      { header: 'Confirmado por', accessor: (a: MedicalAppointment) => a.confirmer?.name ?? '' },
      { header: 'Confirmado em',  accessor: (a: MedicalAppointment) => a.formatted?.confirmedAt ?? '' },
    ],
  })

  const [openForm, setOpenForm] = useState<{ open: boolean; item?: MedicalAppointment }>({ open: false })
  const [openDelete, setOpenDelete] = useState<MedicalAppointment | null>(null)
  const [openConfirm, setOpenConfirm] = useState<MedicalAppointment | null>(null)
  const [openCustom, setOpenCustom] = useState<MedicalAppointment | null>(null)

  async function handleDelete() {
    if (!openDelete) return
    try { await MedicalAppointmentsService.destroy(openDelete.id); toast.success('Consulta removida.'); setOpenDelete(null); setReload(v => v + 1) }
    catch { toast.error('Erro ao remover.') }
  }

  async function downloadPdf(a: MedicalAppointment) {
    try { const url = await MedicalAppointmentsService.confirmationPdf(a.id); window.open(url, '_blank', 'noopener') }
    catch { toast.error('Erro ao gerar comprovante.') }
  }

  const columns: DataTableColumn<MedicalAppointment>[] = [
    { key: 'patient', header: 'Paciente', alwaysVisible: true, className: 'min-w-[280px]', render: a => (
      <div className="flex items-center gap-2">
        <InfoPopover
          align="left"
          ariaLabel="Quem criou e confirmou"
          title="Registro"
          trigger={<Info size={15} className="text-gray-400 hover:text-brand-500" />}
        >
          <div className="space-y-2">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Criado por</div>
              <div className="text-gray-800">{a.creator?.name ?? '—'}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-400">Confirmado por</div>
              {a.confirmer?.name
                ? <div className="text-gray-800">{a.confirmer.name}{a.formatted?.confirmedAt && <span className="text-gray-400"> · {a.formatted.confirmedAt}</span>}</div>
                : <div className="text-gray-400">—</div>}
            </div>
          </div>
        </InfoPopover>
        <span className="font-medium text-gray-800">{a.subscriber?.name ?? '—'}</span>
      </div>
    ) },
    { key: 'provider', header: 'Credenciado / Especialidade', render: a => (
      <div className="leading-tight">
        <div className="text-gray-700">{a.provider?.name ?? '—'}</div>
        <div className="text-xs text-gray-500">{a.specialty?.specialty ?? '—'}</div>
      </div>
    ) },
    { key: 'datetime', header: 'Data e hora', align: 'center', className: 'w-36', render: a => (
      <div className="leading-tight">
        <div className="text-gray-700">{a.formatted?.appointmentDate ?? '—'}</div>
        <div className="text-xs text-gray-500">{a.formatted?.appointmentTime || '—'}</div>
      </div>
    ) },
    { key: 'doctor', header: 'Médico', render: a => <span className="text-gray-600">{a.doctorName ?? '—'}</span> },
    { key: 'status', header: 'Status', align: 'center', className: 'w-36', render: a => <StatusBadge status={a.status} /> },
  ]

  return (
    <>
      <PageHeader
        title="Agendamentos"
        subtitle="Consultas médicas — pendentes e confirmadas"
        crumbs={[{ label: 'Atendimentos' }, { label: 'Agendamentos' }]}
        actions={canCreate ? <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Nova consulta</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="medical.appointments"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        emptyMessage="Nenhuma consulta encontrada."
        rowActions={(a) => a.status === CONFIRMED
          ? [
              ...(canPdf ? [{ key: 'pdf', label: 'Comprovante (PDF)', icon: <FileText size={14} />, onClick: () => downloadPdf(a) }] : []),
              ...(canCustom ? [{ key: 'custom', label: 'Confirmação personalizada', icon: <FileSignature size={14} />, onClick: () => setOpenCustom(a) }] : []),
              ...(canDeleteConfirmed
                ? [{ key: 'delete', label: 'Excluir', icon: <Trash2 size={14} />, danger: true, divider: canPdf || canCustom, onClick: () => setOpenDelete(a) }]
                : []),
            ]
          : [
              ...(canEdit ? [
                { key: 'confirm', label: 'Confirmar', icon: <CheckCircle2 size={14} />, onClick: () => setOpenConfirm(a) },
                { key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => setOpenForm({ open: true, item: a }) },
              ] : []),
              ...(canDelete ? [{ key: 'delete', label: 'Excluir', icon: <Trash2 size={14} />, danger: true, divider: canEdit, onClick: () => setOpenDelete(a) }] : []),
            ]
        }
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      {openForm.open && (
        <AppointmentFormModal
          item={openForm.item}
          onClose={() => setOpenForm({ open: false })}
          onSaved={() => { setOpenForm({ open: false }); setReload(v => v + 1) }}
        />
      )}

      {openConfirm && (
        <ConfirmAppointmentModal
          appointment={openConfirm}
          onClose={() => setOpenConfirm(null)}
          onConfirmed={() => { setOpenConfirm(null); setReload(v => v + 1) }}
        />
      )}

      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover consulta?"
        description={`Remover a consulta de ${openDelete?.subscriber?.name ?? 'paciente'}?`}
        confirmLabel="Excluir"
        variant="danger"
      />

      <CustomConfirmationDrawer
        open={!!openCustom}
        appointment={openCustom}
        onClose={() => setOpenCustom(null)}
        onDone={() => { setOpenCustom(null); setReload(v => v + 1) }}
      />
      {sf.drawer}
    </>
  )
}

// =============================================================================
// Cadastro — wizard (Pendente | Confirmada)
// =============================================================================
// Paciente e credenciado são buscados no servidor conforme se digita: a base
// tem milhares de registros e baixar tudo de uma vez falhava (lista vazia).
const loadPatients = (q: string) => MedicalAppointmentsService.subscriberOptions(q).then(list => list.map(s => ({
  value: s.id,
  label: `${s.name}${s.cpf ? ` · ${maskCpf(s.cpf)}` : ''}${s.type === 'DEPENDENTE' ? ' (dependente)' : ''}`,
})))
const loadProviders = (q: string) => MedicalAppointmentsService.providerOptions(q).then(list => list.map(p => ({ value: p.id, label: p.name })))

function AppointmentFormModal({ item, onClose, onSaved }: {
  item?: MedicalAppointment
  onClose: () => void
  onSaved: () => void
}) {
  const editing = !!item
  const [step, setStep] = useState<1 | 2>(editing ? 2 : 1)
  const [confirmed, setConfirmed] = useState(item?.status === CONFIRMED)

  const [subscriberId, setSubscriberId] = useState(item?.subscriberId ?? '')
  const [subscriberLabel, setSubscriberLabel] = useState(item?.subscriber?.name ?? '')
  const [providerId, setProviderId] = useState(item?.providerId ?? '')
  const [providerLabel, setProviderLabel] = useState(item?.provider?.name ?? '')
  const [specialtyId, setSpecialtyId] = useState(item?.specialtyId ?? '')

  // Especialidades carregam conforme o credenciado selecionado.
  const [specialties, setSpecialties] = useState<Option[]>(
    item?.specialtyId && item?.specialty ? [{ value: item.specialtyId, label: item.specialty.specialty }] : [],
  )
  const [loadingSpecialties, setLoadingSpecialties] = useState(false)

  useEffect(() => {
    if (!providerId) { setSpecialties([]); return }
    setLoadingSpecialties(true)
    MedicalAppointmentsService.providerSpecialties(providerId)
      .then(list => setSpecialties(list.map(s => ({ value: s.id, label: s.specialty }))))
      .catch(() => setSpecialties([]))
      .finally(() => setLoadingSpecialties(false))
  }, [providerId])
  const [date, setDate] = useState(item?.appointmentDate ?? '')
  const [time, setTime] = useState(item?.appointmentTime?.slice(0, 5) ?? '')
  const [doctor, setDoctor] = useState(item?.doctorName ?? '')
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const clearError = (k: string) => setErrors(prev => (prev[k] ? { ...prev, [k]: '' } : prev))

  function validate(): boolean {
    const e: Record<string, string> = {}
    if (!subscriberId) e.subscriberId = 'Selecione o paciente.'
    if (!providerId) e.providerId = 'Selecione o credenciado.'
    if (!specialtyId) e.specialtyId = 'Selecione a especialidade.'
    if (!date) e.date = 'Informe a data.'
    if (confirmed && !time) e.time = 'Informe o horário.'
    if (confirmed && !doctor.trim()) e.doctor = 'Informe o nome do médico.'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function submit() {
    if (!validate()) { toast.error({ title: 'Verifique os campos', description: 'Há campos obrigatórios no formulário.' }); return }
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        subscriber_id: subscriberId,
        provider_id: providerId,
        specialty_id: specialtyId,
        appointment_date: date,
        status: confirmed ? CONFIRMED : PENDING,
      }
      if (confirmed) { payload.appointment_time = time; payload.doctor_name = doctor.trim() }
      const patientLabel = subscriberLabel
      if (editing) { await MedicalAppointmentsService.update(item!.id, payload as never); toast.success({ title: 'Consulta atualizada', description: patientLabel }) }
      else { await MedicalAppointmentsService.create(payload as never); toast.success({ title: 'Consulta cadastrada', description: patientLabel }) }
      onSaved()
    } catch (err) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.')
    } finally { setSaving(false) }
  }

  if (step === 1) {
    return (
      <Modal open onClose={onClose} title="Nova consulta" size="md">
        <p className="text-sm text-gray-600 mb-4">A consulta é pendente ou já está confirmada?</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button type="button" onClick={() => { setConfirmed(false); setStep(2) }}
            className="cursor-pointer text-left rounded-md ring-1 ring-gray-200 p-5 hover:ring-amber-300 hover:bg-amber-50/40 transition flex items-start gap-3">
            <Clock size={22} className="text-amber-500 shrink-0" />
            <span><span className="flex items-center gap-1 text-sm font-semibold text-gray-800">Pendente <ChevronRight size={14} className="text-gray-300" /></span>
              <span className="block text-xs text-gray-500 mt-0.5">Paciente, credenciado, especialidade e data.</span></span>
          </button>
          <button type="button" onClick={() => { setConfirmed(true); setStep(2) }}
            className="cursor-pointer text-left rounded-md ring-1 ring-gray-200 p-5 hover:ring-emerald-300 hover:bg-emerald-50/40 transition flex items-start gap-3">
            <CheckCircle2 size={22} className="text-emerald-500 shrink-0" />
            <span><span className="flex items-center gap-1 text-sm font-semibold text-gray-800">Confirmada <ChevronRight size={14} className="text-gray-300" /></span>
              <span className="block text-xs text-gray-500 mt-0.5">Inclui horário e nome do médico.</span></span>
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal open onClose={onClose} size="md"
      title={editing ? 'Editar consulta' : `Nova consulta ${confirmed ? 'confirmada' : 'pendente'}`}
      footer={<>
        {!editing && <Button variant="ghost" iconLeft={<ArrowLeft size={12} />} onClick={() => setStep(1)}>Voltar</Button>}
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={submit} loading={saving} iconLeft={<Save size={14} />}>Salvar</Button>
      </>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <AsyncSelect label="Paciente" required value={subscriberId} selectedLabel={subscriberLabel} loadOptions={loadPatients}
            onChange={(v, opt) => { setSubscriberId(v); setSubscriberLabel(opt?.label ?? ''); clearError('subscriberId') }}
            placeholder="Selecione o paciente" searchPlaceholder="Nome, CPF ou código" />
          {errors.subscriberId && <p className="mt-1 text-xs text-rose-600">{errors.subscriberId}</p>}
        </div>
        <div>
          <AsyncSelect label="Credenciado" required value={providerId} selectedLabel={providerLabel} loadOptions={loadProviders}
            onChange={(v, opt) => { setProviderId(v); setProviderLabel(opt?.label ?? ''); setSpecialtyId(''); clearError('providerId'); clearError('specialtyId') }}
            placeholder="Selecione o credenciado" searchPlaceholder="Buscar credenciado aceito" />
          {errors.providerId && <p className="mt-1 text-xs text-rose-600">{errors.providerId}</p>}
        </div>
        <Select label="Especialidade" required value={specialtyId} onChange={e => { setSpecialtyId(e.target.value); clearError('specialtyId') }}
          placeholder={!providerId ? 'Selecione o credenciado primeiro' : loadingSpecialties ? 'Carregando' : specialties.length ? 'Selecione a especialidade' : 'Credenciado sem especialidades'}
          disabled={!providerId || loadingSpecialties} options={specialties} error={errors.specialtyId} />
        <Input label="Data da consulta" required type="date" value={date} onChange={e => { setDate(e.target.value); clearError('date') }} clearable={false} error={errors.date} />
        {confirmed && (
          <>
            <Input label="Horário da consulta" required type="time" value={time} onChange={e => { setTime(e.target.value); clearError('time') }} clearable={false} error={errors.time} />
            <div className="md:col-span-2">
              <Input label="Nome do médico" required value={doctor} onChange={e => { setDoctor(e.target.value); clearError('doctor') }} placeholder="Dr(a). " error={errors.doctor} />
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

// =============================================================================
// Confirmar consulta pendente (a partir da lista) — horário + médico
// =============================================================================
function ConfirmAppointmentModal({ appointment, onClose, onConfirmed }: {
  appointment: MedicalAppointment
  onClose: () => void
  onConfirmed: () => void
}) {
  const [time, setTime] = useState(appointment.appointmentTime?.slice(0, 5) ?? '')
  const [doctor, setDoctor] = useState(appointment.doctorName ?? '')
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  async function confirm() {
    const e: Record<string, string> = {}
    if (!time) e.time = 'Informe o horário.'
    if (!doctor.trim()) e.doctor = 'Informe o nome do médico.'
    setErrors(e)
    if (Object.keys(e).length) return
    setSaving(true)
    try {
      await MedicalAppointmentsService.confirm(appointment.id, { appointment_time: time, doctor_name: doctor.trim() })
      toast.success({ title: 'Consulta confirmada', description: appointment.subscriber?.name ?? '' })
      onConfirmed()
    } catch (err) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao confirmar.')
    } finally { setSaving(false) }
  }

  return (
    <Modal open onClose={onClose} size="sm"
      title={`Confirmar consulta — ${appointment.subscriber?.name ?? ''}`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={confirm} loading={saving} iconLeft={<CheckCircle2 size={14} />}>Confirmar</Button>
      </>}
    >
      <p className="text-sm text-gray-600 mb-4">Informe o horário e o médico para confirmar. Após confirmada, a consulta não poderá mais ser alterada.</p>
      <div className="grid grid-cols-1 gap-4">
        <Input label="Horário da consulta" required type="time" value={time} onChange={e => { setTime(e.target.value); setErrors(p => ({ ...p, time: '' })) }} clearable={false} error={errors.time} />
        <Input label="Nome do médico" required value={doctor} onChange={e => { setDoctor(e.target.value); setErrors(p => ({ ...p, doctor: '' })) }} placeholder="Dr(a). " error={errors.doctor} />
      </div>
    </Modal>
  )
}

// =============================================================================
// Confirmação personalizada (PDF) — preservado do fluxo existente
// =============================================================================
function CustomConfirmationDrawer({ open, appointment, onClose, onDone }: {
  open: boolean
  appointment: MedicalAppointment | null
  onClose: () => void
  onDone: () => void
}) {
  const [form, setForm] = useState({
    clinic_laboratory_name: '', confirmation_date: '', confirmation_time: '', address: '', doctor_name: '', price: 0,
  })
  const [saving, setSaving] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !appointment) return
    setPdfUrl(null)
    MedicalAppointmentsService.customConfirmationPrefill(appointment.id)
      .then(cc => setForm({
        clinic_laboratory_name: cc?.clinicLaboratoryName ?? '',
        confirmation_date: cc?.confirmationDate ?? '',
        confirmation_time: cc?.confirmationTime ?? '',
        address: cc?.address ?? '',
        doctor_name: cc?.doctorName ?? appointment.doctorName ?? '',
        price: cc?.price ?? 0,
      }))
      .catch(() => {})
  }, [open, appointment])

  async function submit() {
    if (!appointment) return
    setSaving(true)
    try {
      const res = await MedicalAppointmentsService.createCustomConfirmation(appointment.id, form)
      setPdfUrl(res.url)
      toast.success('Confirmação personalizada gerada.')
      onDone()
    } catch (e) {
      toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao gerar confirmação.')
    } finally { setSaving(false) }
  }

  return (
    <Modal open={open} onClose={onClose} size="lg"
      title={`Confirmação personalizada${appointment ? ` — ${appointment.subscriber?.name ?? ''}` : ''}`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Fechar</Button>
        <Button onClick={submit} loading={saving}>Gerar confirmação</Button>
      </>}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Clínica / Laboratório" value={form.clinic_laboratory_name} onChange={e => setForm(f => ({ ...f, clinic_laboratory_name: e.target.value }))} placeholder="Nome da clínica/laboratório" />
          <Input label="Endereço" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Endereço do atendimento" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input label="Data" type="date" value={form.confirmation_date} onChange={e => setForm(f => ({ ...f, confirmation_date: e.target.value }))} clearable={false} />
          <Input label="Hora" type="time" value={form.confirmation_time} onChange={e => setForm(f => ({ ...f, confirmation_time: e.target.value }))} clearable={false} />
          <CurrencyInput label="Preço" value={form.price} onChange={v => setForm(f => ({ ...f, price: v ?? 0 }))} />
        </div>
        <Input label="Médico" value={form.doctor_name} onChange={e => setForm(f => ({ ...f, doctor_name: e.target.value }))} placeholder="Dr(a). " />
        {pdfUrl && (
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-brand-600 text-sm underline">Abrir PDF gerado</a>
        )}
      </div>
    </Modal>
  )
}
